import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Add,
  ContentCopy,
  Delete,
  DragIndicator,
  Edit,
} from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY } from 'common/constants/services/firms';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import {
  addColumnWithDependencies,
  ColType,
  COLUMN_TYPES,
  DEFAULT_FIRM_SUMMARY_COLUMNS,
  Filter,
  FilterOp,
  getAffectedColumns,
  getValueTypesForSummary,
  SortKey,
  SpreadsheetDefinition,
  SummaryDefinition,
  SummaryGroupBy,
} from 'common/types/summaryDefinitions';
import { DisplayFormatType } from 'common/types/summaryDefinitions/valueTypes/displayFormats';
import { ValueType } from 'common/types/summaryDefinitions/valueTypes/valueType';
import { Tab, TabsNavigation } from 'components';
import { SummaryDefinitionsProvider, useSummaryDefinitions } from 'context/SummaryDefinitionsContext';
import { FIRM_SUMMARY_KEY } from 'pages/Firms/common/constants';
import { FirmPendingDecisions, NoCompanyMessage, NoInformationMessage } from 'pages/Firms/components';
import { useFirmSummaryTab } from 'pages/Firms/hooks';
import { SUPPORTED_FIRM_SUMMARY_COLUMN_TYPES } from 'pages/Firms/hooks/useFirmSummarySheet/config/createColumns';
import { useApproachQueryParam } from 'pages/ValuationsAllocation/hooks';
import { getObjectValue } from 'utilities';
import AddColumnsModal from './AddColumnsModalChecklist';
import EditColumnsPanel from './EditColumnsPanel';
import {
  FilterOpOption,
  getDisplayFormatTypeForColumn,
  getFilterOpsForColumnType,
  isOpValidForFormatType,
  validateFilterValue,
} from './filterValidation';

interface FilterValueInputProps {
  value: string | number;
  formatType: DisplayFormatType;
  disabled?: boolean;
  onChange: (value: string) => void;
  onValidationChange: (valid: boolean) => void;
}

const FilterValueInput: FC<FilterValueInputProps> = ({
  value,
  formatType,
  disabled,
  onChange,
  onValidationChange,
}) => {
  const [localValue, setLocalValue] = useState(String(value ?? ''));
  const validation = validateFilterValue(localValue, formatType);

  useEffect(() => {
    setLocalValue(String(value ?? ''));
  }, [value, formatType]);

  const handleTextChange = (newVal: string) => {
    setLocalValue(newVal);
    onValidationChange(validateFilterValue(newVal, formatType).valid);
  };

  if (formatType === 'boolean') {
    return (
      <FormControl size="small" sx={{ width: 150 }}>
        <InputLabel>Value</InputLabel>
        <Select
          label="Value"
          value={localValue}
          onChange={event => {
            setLocalValue(event.target.value);
            onChange(event.target.value);
            onValidationChange(true);
          }}
          disabled={disabled}>
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      label="Value"
      size="small"
      type={formatType === 'date' ? 'date' : 'text'}
      value={localValue}
      onChange={event => handleTextChange(event.target.value)}
      onBlur={() => onChange(localValue)}
      disabled={disabled}
      error={!validation.valid}
      helperText={validation.error}
      InputLabelProps={formatType === 'date' ? { shrink: true } : undefined}
      sx={{ width: 150 }}
    />
  );
};

function generateCloneName(sourceName: string, existingNames: string[]): string {
  const baseName = sourceName.replace(/\s*\(Copy(?:\s+\d+)?\)\s*$/i, '').trim();
  const nameSet = new Set(existingNames.map(n => n.toLowerCase()));

  const firstCandidate = `${baseName} (Copy)`;
  if (!nameSet.has(firstCandidate.toLowerCase())) {
    return firstCandidate;
  }

  let counter = 2;
  while (nameSet.has(`${baseName} (Copy ${counter})`.toLowerCase())) {
    counter++;
  }
  return `${baseName} (Copy ${counter})`;
}

const SortableListItem: FC<{
  id: string;
  children: React.ReactNode;
  secondaryAction?: React.ReactNode;
  disabled?: boolean;
}> = ({ id, children, secondaryAction, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: `${transition}, transform 200ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
    opacity: isDragging ? 0 : 1,
    cursor: isDragging ? 'grabbing' : undefined,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      secondaryAction={secondaryAction}
      sx={{
        alignItems: 'flex-start',
        borderRadius: 1,
        transition: 'background-color 150ms ease',
        ...(isDragging && {
          backgroundColor: 'action.hover',
          boxShadow: 2,
        }),
      }}>
      <ListItemIcon
        sx={{
          minWidth: 32,
          alignSelf: 'center',
          cursor: disabled ? 'not-allowed' : 'grab',
          color: 'text.secondary',
          borderRadius: 1,
          transition: 'background-color 150ms ease',
          ...(!disabled && {
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }),
        }}
        {...(disabled ? {} : { ...attributes, ...listeners })}>
        <DragIndicator fontSize="small" />
      </ListItemIcon>
      {children}
    </ListItem>
  );
};

const FirmSummaryTabs: FC = () => {
  const queryClient = useQueryClient();

  const sortableSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const {
    orderedDefinitions,
    activeDefinition,
    setActiveDefinition,
    createDefinition,
    cloneDefinition,
    deleteDefinition,
    updateDefinition,
    refetch,
    isLoading,
    isSaving,
  } = useSummaryDefinitions();
  const { tabId, setApproachQueryParam } = useApproachQueryParam();

  // When the last summary definition is deleted, clear the tab query param and fall back to default tab
  useEffect(() => {
    const tabIdStr = typeof tabId === 'string' ? tabId : null;
    if (orderedDefinitions.length === 0 && tabIdStr?.startsWith('summary-definition-')) {
      setApproachQueryParam(FIRM_SUMMARY_KEY);
    }
  }, [orderedDefinitions.length, setApproachQueryParam, tabId]);

  // Get the spreadsheet definition from the active definition
  const spreadsheetDefinition = activeDefinition?.spreadsheet_definition ?? activeDefinition?.spreadsheetDefinition;
  const [currentTabMenu, setCurrentTabMenu] = useState<HTMLSpanElement | null>(null);
  const [isColumnEditorOpen, setIsColumnEditorOpen] = useState(false);
  const [isAddColumnsModalOpen, setIsAddColumnsModalOpen] = useState(false);
  const [isCreatingNewView, setIsCreatingNewView] = useState(false);
  const addTabClickedRef = useRef(false);
  const [editorName, setEditorName] = useState('');
  const [editorColumns, setEditorColumns] = useState<ColType[]>([]);
  const [selectedColumnsToAdd, setSelectedColumnsToAdd] = useState<ValueType[]>([]);

  const [editorGroupBy, setEditorGroupBy] = useState<SummaryGroupBy>('company');
  const [editorSortKeys, setEditorSortKeys] = useState<SortKey[]>([]);
  const [editorFilters, setEditorFilters] = useState<Filter[]>([]);
  const [filterInputValidities, setFilterInputValidities] = useState<boolean[]>([]);
  const [editorBreakevenMultiple, setEditorBreakevenMultiple] = useState<string>('1');
  const [isEditorSaving, setIsEditorSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState<{ columnType: ColType; dependents: ColType[] } | null>(null);

  const availableValueTypes = useMemo(() => getValueTypesForSummary('firm'), []);
  const valueTypeById = useMemo(
    () => new Map(availableValueTypes.map(valueType => [valueType.id, valueType])),
    [availableValueTypes]
  );
  const requiredColumnIds = useMemo(
    () => availableValueTypes.filter(valueType => valueType.metadata?.isRequired).map(valueType => valueType.id),
    [availableValueTypes]
  );

  const requiredColumnsForEditor = useMemo(
    () => new Set([...requiredColumnIds, 'company_name', 'fund_name']),
    [requiredColumnIds]
  );

  const hasBreakevenColumn = useMemo(
    () => editorColumns.includes(COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE),
    [editorColumns]
  );
  const breakevenMultipleValue = Number(editorBreakevenMultiple);
  const isBreakevenMultipleInvalid =
    hasBreakevenColumn && (!Number.isFinite(breakevenMultipleValue) || breakevenMultipleValue <= 0);
  const filterOptionsPerFilter = useMemo<FilterOpOption[][]>(
    () =>
      editorFilters.map(([column]) => {
        const formatType = getDisplayFormatTypeForColumn(column, valueTypeById);
        return getFilterOpsForColumnType(formatType);
      }),
    [editorFilters, valueTypeById]
  );

  const filterValidations = useMemo(
    () =>
      editorFilters.map(([column, [, value]]) => {
        const formatType = getDisplayFormatTypeForColumn(column, valueTypeById);
        return validateFilterValue(value, formatType);
      }),
    [editorFilters, valueTypeById]
  );

  const hasInvalidFilters = useMemo(
    () => filterValidations.some(v => !v.valid) || filterInputValidities.some(v => !v),
    [filterValidations, filterInputValidities]
  );

  const handleFilterValidationChange = useCallback((index: number, valid: boolean) => {
    setFilterInputValidities(prev => prev.map((v, idx) => (idx === index ? valid : v)));
  }, []);

  const handleMenuClose = useCallback(() => setCurrentTabMenu(null), []);

  const handleMenuOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setCurrentTabMenu(event.currentTarget as HTMLSpanElement);
    },
    []
  );

  const handleCloneDefinition = useCallback(async (definitionId: number) => {
    if (isSaving) {
      return;
    }
    const definitionToClone = orderedDefinitions.find(d => d.id === definitionId);
    if (!definitionToClone) {
      return;
    }
    handleMenuClose();
    const existingNames = orderedDefinitions.map(d => d.name);
    const clonedDefinition = await cloneDefinition(definitionId, generateCloneName(definitionToClone.name, existingNames));
    if (clonedDefinition) {
      setActiveDefinition(clonedDefinition.id);
      setApproachQueryParam(`summary-definition-${clonedDefinition.id}`);
    }
  }, [
    orderedDefinitions,
    cloneDefinition,
    isSaving,
    setActiveDefinition,
    handleMenuClose,
    setApproachQueryParam,
  ]);

  const handleDeleteDefinition = useCallback(async (definitionId: number) => {
    handleMenuClose();
    await deleteDefinition(definitionId);
  }, [deleteDefinition, handleMenuClose]);

  const normalizeDefinition = useCallback((definition?: SpreadsheetDefinition): SpreadsheetDefinition => {
    const fallbackColumns = Object.fromEntries(DEFAULT_FIRM_SUMMARY_COLUMNS.map((col, idx) => [idx, col]));
    return {
      columns: definition?.columns ?? fallbackColumns,
      sortKeys: definition?.sortKeys ?? [],
      groupBy: definition?.groupBy ?? 'company',
      filters: definition?.filters ?? [],
      filterExclusions: definition?.filterExclusions ?? [],
      breakevenMultiple: definition?.breakevenMultiple ?? 1,
    };
  }, []);

  const getOrderedColumnTypes = useCallback((definition?: SpreadsheetDefinition): ColType[] => {
    if (!definition?.columns || Object.keys(definition.columns).length === 0) {
      return DEFAULT_FIRM_SUMMARY_COLUMNS;
    }

    return Object.entries(definition.columns)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([, colType]) => colType);
  }, []);

  const canEditDefinition = useCallback(
    (definition?: SummaryDefinition | typeof orderedDefinitions[number] | null) => {
      if (!definition) {
        return false;
      }
      if ('isGlobal' in definition && definition.isGlobal) {
        return Boolean(definition.firm || definition.fund);
      }
      return true;
    }, []);

  const handleSortToggle = useCallback(
    async (columnType: ColType) => {
      if (!activeDefinition || !canEditDefinition(activeDefinition)) {
        return;
      }

      const currentDef = normalizeDefinition(
        activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition
      );
      const currentSortKeys = currentDef.sortKeys ?? [];
      const existingIndex = currentSortKeys.findIndex(([col]) => col === columnType);

      let nextSortKeys: SortKey[];
      if (existingIndex === -1) {
        nextSortKeys = [...currentSortKeys, [columnType, 'asc']];
      } else if (currentSortKeys[existingIndex][1] === 'asc') {
        nextSortKeys = currentSortKeys.map((sk, idx) =>
          idx === existingIndex ? [sk[0], 'desc'] as SortKey : sk
        );
      } else {
        nextSortKeys = currentSortKeys.filter((_, idx) => idx !== existingIndex);
      }

      const updatedDef: SpreadsheetDefinition = { ...currentDef, sortKeys: nextSortKeys };

      await updateDefinition(activeDefinition.id, { spreadsheet_definition: updatedDef });
      await Promise.all([
        refetch(),
        queryClient.invalidateQueries({ queryKey: [FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY] }),
      ]);
    },
    [activeDefinition, canEditDefinition, normalizeDefinition, queryClient, refetch, updateDefinition]
  );

  const handleClearFilters = useCallback(async () => {
    if (!activeDefinition) {
      return;
    }
    const currentDef = normalizeDefinition(
      activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition
    );
    await updateDefinition(activeDefinition.id, {
      spreadsheet_definition: { ...currentDef, filters: [] },
    });
    await Promise.all([
      refetch(),
      queryClient.invalidateQueries({ queryKey: [FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY] }),
    ]);
  }, [activeDefinition, normalizeDefinition, queryClient, refetch, updateDefinition]);

  const handleClearSingleFilter = useCallback(
    async (index: number) => {
      if (!activeDefinition) {
        return;
      }
      const currentDef = normalizeDefinition(
        activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition
      );
      const updatedFilters = (currentDef.filters ?? []).filter((_, idx) => idx !== index);
      await updateDefinition(activeDefinition.id, {
        spreadsheet_definition: { ...currentDef, filters: updatedFilters },
      });
      await Promise.all([
        refetch(),
        queryClient.invalidateQueries({ queryKey: [FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY] }),
      ]);
    },
    [activeDefinition, normalizeDefinition, queryClient, refetch, updateDefinition]
  );

  const handleClearSorts = useCallback(async () => {
    if (!activeDefinition) {
      return;
    }
    const currentDef = normalizeDefinition(
      activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition
    );
    await updateDefinition(activeDefinition.id, {
      spreadsheet_definition: { ...currentDef, sortKeys: [] },
    });
    await Promise.all([
      refetch(),
      queryClient.invalidateQueries({ queryKey: [FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY] }),
    ]);
  }, [activeDefinition, normalizeDefinition, queryClient, refetch, updateDefinition]);

  const handleClearSingleSort = useCallback(
    async (columnType: ColType) => {
      if (!activeDefinition) {
        return;
      }
      const currentDef = normalizeDefinition(
        activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition
      );
      const updatedSortKeys = (currentDef.sortKeys ?? []).filter(([col]) => col !== columnType);
      await updateDefinition(activeDefinition.id, {
        spreadsheet_definition: { ...currentDef, sortKeys: updatedSortKeys },
      });
      await Promise.all([
        refetch(),
        queryClient.invalidateQueries({ queryKey: [FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY] }),
      ]);
    },
    [activeDefinition, normalizeDefinition, queryClient, refetch, updateDefinition]
  );

  const openColumnEditor = useCallback(() => {
    if (!activeDefinition || !canEditDefinition(activeDefinition)) {
      return;
    }

    const currentDefinition = normalizeDefinition(
      activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition
    );
    const orderedColumns = getOrderedColumnTypes(currentDefinition);
    const withRequired = [...orderedColumns];

    // Ensure company_name and fund_name are always present
    ['company_name', ...requiredColumnIds].forEach(requiredId => {
      if (!withRequired.includes(requiredId)) {
        withRequired.unshift(requiredId);
      }
    });

    setEditorColumns(withRequired);
    setEditorGroupBy(currentDefinition.groupBy ?? 'company');
    setEditorSortKeys(currentDefinition.sortKeys ?? []);
    const initialFilters = currentDefinition.filters ?? [];
    setEditorFilters(initialFilters);
    setFilterInputValidities(initialFilters.map(() => true));
    setEditorBreakevenMultiple(`${currentDefinition.breakevenMultiple ?? 1}`);
    setEditorName(activeDefinition.name ?? '');
    setIsColumnEditorOpen(true);
  }, [activeDefinition, canEditDefinition, getOrderedColumnTypes, normalizeDefinition, requiredColumnIds]);

  const handleOpenAddColumnsModal = useCallback(() => setIsAddColumnsModalOpen(true), []);

  const { firmSummaryTab, hasNoCompanies, hasNoInvestments, isFilteredEmpty } = useFirmSummaryTab({
    spreadsheetDefinition,
    definitionId: activeDefinition?.id,
    onSortToggle: handleSortToggle,
    valueTypeById,
    onClearFilters: handleClearFilters,
    onRemoveFilter: handleClearSingleFilter,
    onClearSorts: handleClearSorts,
    onRemoveSort: handleClearSingleSort,
    onAddColumn: handleOpenAddColumnsModal,
  });

  const closeColumnEditor = useCallback(() => {
    setIsColumnEditorOpen(false);
    setIsCreatingNewView(false);
    setEditorName('');
    setEditorColumns([]);
    setSelectedColumnsToAdd([]);
    setEditorGroupBy('company');
    setEditorSortKeys([]);
    setEditorFilters([]);
    setFilterInputValidities([]);
    setEditorBreakevenMultiple('1');
    setIsEditorSaving(false);
    setRemoveConfirm(null);
  }, []);

  const handleColumnsToAddChange = useCallback((selected: ValueType[]) => {
    setSelectedColumnsToAdd(selected);
    
    let tempDefinition: SpreadsheetDefinition = {
      columns: Object.fromEntries(editorColumns.map((col, idx) => [idx, col])),
      sortKeys: [],
      groupBy: 'company',
      filters: [],
      filterExclusions: [],
    };

    selected.forEach(valueType => {
      if (SUPPORTED_FIRM_SUMMARY_COLUMN_TYPES.has(valueType.id)) {
        tempDefinition = addColumnWithDependencies(tempDefinition, valueType.id);
      }
    });

    const newColumns = Object.entries(tempDefinition.columns)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([, colType]) => colType);

    setEditorColumns(newColumns);
  }, [editorColumns]);

  const handleAddColumnsFromModal = useCallback(async (newColumns: ColType[]) => {
    if (!activeDefinition) return;

    const currentDef = normalizeDefinition(
      activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition
    );

    const updatedDef = {
      ...currentDef,
      columns: Object.fromEntries(newColumns.map((col, idx) => [idx, col])),
    };

    await updateDefinition(activeDefinition.id, {
      spreadsheet_definition: updatedDef,
    });

    await Promise.all([
      refetch(),
      queryClient.invalidateQueries({ queryKey: [FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY] }),
    ]);
  }, [activeDefinition, normalizeDefinition, updateDefinition, refetch, queryClient]);


  const columnIds = useMemo(() => editorColumns.map((_, i) => `column-${i}`), [editorColumns]);
  const sortIds = useMemo(() => editorSortKeys.map((_, i) => `sort-${i}`), [editorSortKeys]);
  const filterIds = useMemo(() => editorFilters.map((_, i) => `filter-${i}`), [editorFilters]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  }, []);

  const handleReorderColumns = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = columnIds.indexOf(String(active.id));
      const newIndex = columnIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      setEditorColumns(prev => arrayMove(prev, oldIndex, newIndex));
    },
    [columnIds]
  );

  const handleReorderSortKeys = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = sortIds.indexOf(String(active.id));
      const newIndex = sortIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      setEditorSortKeys(prev => arrayMove(prev, oldIndex, newIndex));
    },
    [sortIds]
  );

  const handleReorderFilters = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = filterIds.indexOf(String(active.id));
      const newIndex = filterIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      setEditorFilters(prev => arrayMove(prev, oldIndex, newIndex));
      setFilterInputValidities(prev => arrayMove(prev, oldIndex, newIndex));
    },
    [filterIds]
  );

  const handleRemoveColumn = useCallback(
    (columnType: ColType) => {
      if (requiredColumnIds.includes(columnType)) {
        return;
      }

      const draftDefinition = normalizeDefinition({
        columns: Object.fromEntries(editorColumns.map((col, idx) => [idx, col])),
        sortKeys: [],
        groupBy: 'company',
        filters: [],
        filterExclusions: [],
      });
      const dependents = getAffectedColumns(draftDefinition, columnType);
      const affected = dependents.length > 0 ? [columnType, ...dependents] : [columnType];

      if (dependents.length > 0) {
        setRemoveConfirm({ columnType, dependents });
        return;
      }

      setEditorColumns(prev => prev.filter(col => !affected.includes(col)));
    },
    [editorColumns, normalizeDefinition, requiredColumnIds]
  );

  const handleAddSortKey = useCallback(() => {
    const defaultColumn = editorColumns[0];
    if (!defaultColumn) {
      return;
    }
    setEditorSortKeys(prev => [...prev, [defaultColumn, 'asc']]);
  }, [editorColumns]);

  const handleUpdateSortColumn = useCallback((index: number, column: ColType) => {
    setEditorSortKeys(prev => prev.map((sortKey, idx) => (idx === index ? [column, sortKey[1]] : sortKey)));
  }, []);

  const handleUpdateSortDirection = useCallback((index: number, direction: SortKey[1]) => {
    setEditorSortKeys(prev => prev.map((sortKey, idx) => (idx === index ? [sortKey[0], direction] : sortKey)));
  }, []);

  const handleRemoveSortKey = useCallback((index: number) => {
    setEditorSortKeys(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleAddFilter = useCallback(() => {
    const defaultColumn = editorColumns[0];
    if (!defaultColumn) {
      return;
    }
    setEditorFilters(prev => [...prev, [defaultColumn, ['=', '']] as Filter]);
    setFilterInputValidities(prev => [...prev, true]);
  }, [editorColumns]);

  const handleUpdateFilterColumn = useCallback(
    (index: number, column: ColType) => {
      setEditorFilters(prev =>
        prev.map((filter, idx) => {
          if (idx !== index) return filter;
          const [, [currentOp, currentValue]] = filter;
          const newFormatType = getDisplayFormatTypeForColumn(column, valueTypeById);
          const validOps = getFilterOpsForColumnType(newFormatType);
          const nextOp = isOpValidForFormatType(currentOp, newFormatType) ? currentOp : validOps[0].value;
          const valueValid = validateFilterValue(currentValue, newFormatType).valid;
          const nextValue = valueValid ? currentValue : '';
          return [column, [nextOp, nextValue]] as Filter;
        })
      );
    },
    [valueTypeById]
  );

  const handleUpdateFilterOp = useCallback((index: number, op: FilterOp) => {
    setEditorFilters(prev => prev.map((filter, idx) => (idx === index ? [filter[0], [op, filter[1][1]]] : filter)));
  }, []);

  const handleUpdateFilterValue = useCallback((index: number, value: string | number) => {
    setEditorFilters(prev => prev.map((filter, idx) => (idx === index ? [filter[0], [filter[1][0], value]] : filter)));
  }, []);

  const handleRemoveFilter = useCallback((index: number) => {
    setEditorFilters(prev => prev.filter((_, idx) => idx !== index));
    setFilterInputValidities(prev => prev.filter((_, idx) => idx !== index));
  }, []);

  const handleConfirmRemove = useCallback(() => {
    if (!removeConfirm) {
      return;
    }

    const affected = [removeConfirm.columnType, ...removeConfirm.dependents];
    setEditorColumns(prev => prev.filter(col => !affected.includes(col)));
    setRemoveConfirm(null);
  }, [removeConfirm]);

  const handleCancelRemove = useCallback(() => {
    setRemoveConfirm(null);
  }, []);

  const handleSaveColumns = useCallback(async () => {
    if (isEditorSaving || isBreakevenMultipleInvalid || hasInvalidFilters) {
      return;
    }

    const trimmedName = editorName.trim();
    if (!trimmedName) {
      return;
    }

    const validColumns = new Set(editorColumns);
    const sanitizedSortKeys = editorSortKeys.filter(([col]) => validColumns.has(col));
    const sanitizedFilters = editorFilters.filter(
      ([col, [, value]]) => validColumns.has(col) && value !== null && typeof value !== 'undefined' && `${value}` !== ''
    );
    
    const nextDefinition: SpreadsheetDefinition = {
      columns: Object.fromEntries(editorColumns.map((col, idx) => [idx, col])),
      groupBy: editorGroupBy,
      sortKeys: sanitizedSortKeys,
      filters: sanitizedFilters,
      breakevenMultiple: hasBreakevenColumn ? breakevenMultipleValue : 1,
      filterExclusions: [],
    };

    try {
      setIsEditorSaving(true);
      
      if (isCreatingNewView) {
        const newDefinition = await createDefinition(trimmedName, nextDefinition);
        if (newDefinition) {
          setActiveDefinition(newDefinition.id);
          setApproachQueryParam(`summary-definition-${newDefinition.id}`);
        }
      } else if (activeDefinition) {
        const baseDefinition = normalizeDefinition(
          activeDefinition.spreadsheet_definition ?? activeDefinition.spreadsheetDefinition
        );
        await updateDefinition(activeDefinition.id, {
          name: trimmedName,
          spreadsheet_definition: { ...baseDefinition, ...nextDefinition },
        });
      }
      
      await Promise.all([
        refetch(),
        queryClient.invalidateQueries({ queryKey: [FIRM_SUMMARY_DEFINITION_DATA_QUERY_KEY] }),
      ]);
      closeColumnEditor();
    } finally {
      setIsEditorSaving(false);
    }
  }, [
    isCreatingNewView,
    activeDefinition,
    closeColumnEditor,
    createDefinition,
    editorColumns,
    editorFilters,
    editorGroupBy,
    editorName,
    isEditorSaving,
    isBreakevenMultipleInvalid,
    hasInvalidFilters,
    normalizeDefinition,
    queryClient,
    refetch,
    editorSortKeys,
    hasBreakevenColumn,
    breakevenMultipleValue,
    updateDefinition,
    setActiveDefinition,
    setApproachQueryParam,
  ]);

  // Build menu items for the active tab
  const buildMenuItems = useCallback(
    (definition: typeof orderedDefinitions[number]) => {
      const items = [
        <MenuItem key="clone" onClick={() => handleCloneDefinition(definition.id)} disabled={isSaving}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Clone</ListItemText>
        </MenuItem>,
      ];

      // Only allow deletion for non-global definitions
      if (canEditDefinition(definition)) {
        items.unshift(
          <MenuItem
            key="edit-columns"
            onClick={() => {
              handleMenuClose();
              openColumnEditor();
            }}
            disabled={isSaving || isEditorSaving}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Summary</ListItemText>
          </MenuItem>
        );
        items.push(
          <Divider key="divider" />,
          <MenuItem key="delete" onClick={() => handleDeleteDefinition(definition.id)} disabled={isSaving}>
            <ListItemIcon sx={{ color: 'error.main', minWidth: 36 }}>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
          </MenuItem>
        );
      }

      return items;
    },
    [
      canEditDefinition,
      handleCloneDefinition,
      handleDeleteDefinition,
      handleMenuClose,
      isEditorSaving,
      isSaving,
      openColumnEditor,
    ]
  );

  const tabs = useMemo((): Tab[] => {
    // Build the "+" action tab for creating new definitions (only when custom views enabled)
    const addTab: Tab = {
      id: 'add-new-definition',
      label: (
        <span
          aria-hidden="true"
          onMouseDown={() => {
            addTabClickedRef.current = true;
          }}
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          +
        </span>
      ),
      isActionTab: true,
      content: null,
    };

    if (!orderedDefinitions.length) {
      return [firmSummaryTab, addTab];
    }

    const definitionTabs: Tab[] = orderedDefinitions.map(definition => ({
      ...firmSummaryTab,
      id: `summary-definition-${definition.id}`,
      label: definition.name,
      menuItems: buildMenuItems(definition),
    }));

    // Add "+" tab at the end
    definitionTabs.push(addTab);

    return definitionTabs;
  }, [firmSummaryTab, orderedDefinitions, buildMenuItems]);

  const handleActiveTabChange = useCallback(
    (tabToActivate?: Tab) => {
      if (!tabToActivate?.id) {
        return;
      }

      // Handle the "+" tab — only if explicitly clicked, not from reflow
      if (tabToActivate.id === 'add-new-definition') {
        if (!addTabClickedRef.current) {
          return;
        }
        addTabClickedRef.current = false;
        
        // Open modal for new view (don't create definition yet)
        setIsCreatingNewView(true);
        setEditorColumns([...DEFAULT_FIRM_SUMMARY_COLUMNS]);
        setEditorGroupBy('company');
        setEditorSortKeys([]);
        setEditorFilters([]);
        setFilterInputValidities([]);
        setEditorBreakevenMultiple('1');
        const existingNames = orderedDefinitions.map(d => d.name);
        let newName = 'New View';
        if (existingNames.some(n => n.toLowerCase() === newName.toLowerCase())) {
          newName = generateCloneName(newName, existingNames);
        }
        setEditorName(newName);
        setIsColumnEditorOpen(true);
        return;
      }

      const id = tabToActivate.id.replace('summary-definition-', '');
      const parsedId = id ? Number(id) : NaN;

      if (!Number.isNaN(parsedId) && parsedId !== activeDefinition?.id) {
        closeColumnEditor();
        setActiveDefinition(parsedId);
      } else if (Number.isNaN(parsedId)) {
        closeColumnEditor();
        setActiveDefinition(null);
      }
      // If parsedId === activeDefinition?.id, do nothing (tab array updated but same tab is active)
    },
    [activeDefinition?.id, closeColumnEditor, setActiveDefinition, orderedDefinitions]
  );

  return (
    <>
      <TabsNavigation
        isDisabled={isLoading || isSaving}
        handleMenuClose={handleMenuClose}
        handleMenuOpen={handleMenuOpen}
        menuAnchorEl={currentTabMenu}
        onActiveTabChange={handleActiveTabChange}
        mountOnActive
        tabs={tabs}
        updateUrlOnTabChange>
        {/* Pending decisions */}
        <FirmPendingDecisions />

        {/* Add company message */}
        {hasNoCompanies && <NoCompanyMessage />}

        {/* Add investment message */}
        {!hasNoCompanies && hasNoInvestments && !isFilteredEmpty && <NoInformationMessage />}
      </TabsNavigation>

      <Dialog
        maxWidth="md"
        fullWidth
        open={isColumnEditorOpen}
        onClose={closeColumnEditor}>
        <DialogTitle>
          {isCreatingNewView ? 'Create Summary View' : 'Edit Summary'}
          {isCreatingNewView && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Name your view, choose which columns to display, and drag to reorder them.
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={editorName}
            onChange={event => setEditorName(event.target.value)}
            disabled={isEditorSaving}
            error={isColumnEditorOpen && editorName.trim().length === 0}
            helperText={isColumnEditorOpen && editorName.trim().length === 0 ? 'Name is required.' : ' '}
          />

          <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
            Columns
          </Typography>
          <EditColumnsPanel
            availableColumns={availableValueTypes.filter(vt => SUPPORTED_FIRM_SUMMARY_COLUMN_TYPES.has(vt.id))}
            allValueTypes={valueTypeById}
            columns={editorColumns}
            requiredColumns={requiredColumnsForEditor}
            onChange={setEditorColumns}
            disabled={isEditorSaving}
          />

          {/* TODO: breakevenMultiple is not persisted or used in enhanced mode.
              Re-enable this input when dynamic breakeven recomputation is implemented. */}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
            Sort order
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginBottom: 1 }}>
            Sorting uses the company's total values across all funds, not individual fund-level values.
          </Typography>
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={handleAddSortKey}
            disabled={isEditorSaving || editorColumns.length === 0}
            sx={{ marginBottom: 2 }}>
            Add Sort Key
          </Button>

          <List dense>
            <DndContext
              sensors={sortableSensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleReorderSortKeys}>
              <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
                {editorSortKeys.map(([column, direction], index) => (
                  <SortableListItem key={`sort-${index}`} id={`sort-${index}`} disabled={isEditorSaving}>
                    <Stack
                      alignItems="center"
                      direction="row"
                      spacing={1}
                      sx={{ width: '100%', minWidth: 0 }}>
                      <FormControl fullWidth size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id={`sort-column-${index}`}>Column</InputLabel>
                        <Select
                          labelId={`sort-column-${index}`}
                          label="Column"
                          value={column}
                          onChange={event => handleUpdateSortColumn(index, event.target.value as ColType)}
                          disabled={isEditorSaving}>
                          {editorColumns.map(col => (
                            <MenuItem key={`sort-col-${col}`} value={col}>
                              {valueTypeById.get(col)?.displayName ?? col}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id={`sort-direction-${index}`}>Direction</InputLabel>
                        <Select
                          labelId={`sort-direction-${index}`}
                          label="Direction"
                          value={direction}
                          onChange={event => handleUpdateSortDirection(index, event.target.value as SortKey[1])}
                          disabled={isEditorSaving}>
                          <MenuItem value="asc">Ascending</MenuItem>
                          <MenuItem value="desc">Descending</MenuItem>
                        </Select>
                      </FormControl>

                      <IconButton
                        onClick={() => handleRemoveSortKey(index)}
                        disabled={isEditorSaving}
                        aria-label="Remove sort key"
                        sx={{ flexShrink: 0 }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  </SortableListItem>
                ))}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragId?.startsWith('sort-') ? (
                  (() => {
                    const idx = parseInt(activeDragId.replace('sort-', ''), 10);
                    const [column, direction] = editorSortKeys[idx] ?? [];
                    const displayName = valueTypeById.get(column)?.displayName ?? column;
                    return (
                      <Paper
                        elevation={8}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: 'grabbing',
                          borderRadius: 1,
                          minWidth: 220,
                          transform: 'rotate(2deg)',
                          boxShadow: 3,
                        }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DragIndicator fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={500}>
                            {displayName} ({direction === 'asc' ? 'Asc' : 'Desc'})
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })()
                ) : null}
              </DragOverlay>
            </DndContext>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
            Filters
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginBottom: 1 }}>
            Filtering uses the company's total values across all funds, not individual fund-level values.
          </Typography>
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={handleAddFilter}
            disabled={isEditorSaving || editorColumns.length === 0}
            sx={{ marginBottom: 2 }}>
            Add Filter
          </Button>

          <List dense>
            <DndContext
              sensors={sortableSensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleReorderFilters}>
              <SortableContext items={filterIds} strategy={verticalListSortingStrategy}>
                {editorFilters.map(([column, [op, value]], index) => {
                  const formatType = getDisplayFormatTypeForColumn(column, valueTypeById);
                  const filterOps = filterOptionsPerFilter[index] ?? [];
                  return (
                    <SortableListItem
                      key={`filter-${index}`}
                      id={`filter-${index}`}
                      disabled={isEditorSaving}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveFilter(index)}
                          disabled={isEditorSaving}
                          aria-label="Remove filter">
                          <Delete fontSize="small" />
                        </IconButton>
                      }>
                      <Stack alignItems="center" direction="row" spacing={1} sx={{ width: '100%' }}>
                        <FormControl fullWidth size="small" sx={{ width: 160 }}>
                          <InputLabel id={`filter-column-${index}`}>Column</InputLabel>
                          <Select
                            labelId={`filter-column-${index}`}
                            label="Column"
                            value={column}
                            onChange={event => handleUpdateFilterColumn(index, event.target.value as ColType)}
                            disabled={isEditorSaving}>
                            {editorColumns.map(col => (
                              <MenuItem key={`filter-col-${col}`} value={col}>
                                {valueTypeById.get(col)?.displayName ?? col}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl fullWidth size="small" sx={{ width: 130 }}>
                          <InputLabel id={`filter-op-${index}`}>Condition</InputLabel>
                          <Select
                            labelId={`filter-op-${index}`}
                            label="Condition"
                            value={op}
                            onChange={event => handleUpdateFilterOp(index, event.target.value as FilterOp)}
                            disabled={isEditorSaving}>
                            {filterOps.map(option => (
                              <MenuItem key={`filter-op-${option.value}`} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FilterValueInput
                          value={value}
                          formatType={formatType}
                          disabled={isEditorSaving}
                          onChange={newValue => handleUpdateFilterValue(index, newValue)}
                          onValidationChange={valid => handleFilterValidationChange(index, valid)}
                        />
                      </Stack>
                    </SortableListItem>
                  );
                })}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragId?.startsWith('filter-') ? (
                  (() => {
                    const idx = parseInt(activeDragId.replace('filter-', ''), 10);
                    const [column, [op, value]] = editorFilters[idx] ?? [];
                    const displayName = valueTypeById.get(column)?.displayName ?? column;
                    const dragFormatType = getDisplayFormatTypeForColumn(column, valueTypeById);
                    const dragOps = getFilterOpsForColumnType(dragFormatType);
                    const opLabel = dragOps.find(o => o.value === op)?.label ?? op;
                    return (
                      <Paper
                        elevation={8}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: 'grabbing',
                          borderRadius: 1,
                          minWidth: 240,
                          transform: 'rotate(2deg)',
                          boxShadow: 3,
                        }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DragIndicator fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={500}>
                            {displayName} {opLabel} {String(value || '')}
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })()
                ) : null}
              </DragOverlay>
            </DndContext>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeColumnEditor} disabled={isEditorSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveColumns}
            variant="contained"
            disabled={
              isEditorSaving ||
              editorColumns.length === 0 ||
              editorName.trim().length === 0 ||
              isBreakevenMultipleInvalid ||
              hasInvalidFilters
            }>
            {isCreatingNewView ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth="xs"
        fullWidth
        open={Boolean(removeConfirm)}
        onClose={handleCancelRemove}>
        <DialogTitle>Remove dependent columns?</DialogTitle>
        <DialogContent dividers>
          {removeConfirm ? (
            <List dense>
              <ListItem>
                <ListItemText
                  primary={valueTypeById.get(removeConfirm.columnType)?.displayName ?? removeConfirm.columnType}
                  secondary="Selected column"
                />
              </ListItem>
              {removeConfirm.dependents.map(dependent => (
                <ListItem key={dependent}>
                  <ListItemText
                    primary={valueTypeById.get(dependent)?.displayName ?? dependent}
                    secondary="Dependent column"
                  />
                </ListItem>
              ))}
            </List>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmRemove}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <AddColumnsModal
        open={isAddColumnsModalOpen}
        onClose={() => setIsAddColumnsModalOpen(false)}
        onSave={handleAddColumnsFromModal}
        availableColumns={availableValueTypes.filter(vt => SUPPORTED_FIRM_SUMMARY_COLUMN_TYPES.has(vt.id))}
        currentColumns={
          spreadsheetDefinition?.columns
            ? Object.values(spreadsheetDefinition.columns) as ColType[]
            : DEFAULT_FIRM_SUMMARY_COLUMNS as ColType[]
        }
        groupBy={spreadsheetDefinition?.groupBy}
        disabled={isSaving}
      />
    </>
  );
};

const FirmSummary: FC = () => {
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo } = getObjectValue(storeValue);
  const firmId = getObjectValue(firmInfo).id;

  if (!firmId) {
    return null;
  }

  return (
    <SummaryDefinitionsProvider entityId={firmId} summaryType="firm" fetchEnabled>
      <FirmSummaryTabs />
    </SummaryDefinitionsProvider>
  );
};

export default FirmSummary;
